/**
 * 地图编辑器右侧操作记录列表
 *
 * 职责：
 * 1. 以分页卡片形式展示动态地图变量列表（variableList）
 * 2. 响应卡片点击：高亮地图对应多边形并同步右侧选中态
 * 3. 提供启用/停用、编辑、删除等快捷操作的入口，并通过 props 回调给上层处理
 *
 * 注意：列表选中态 selectedItem 与父组件传入的 curSelected 保持同步，
 *       以便地图点击选中后右侧列表能自动翻页并高亮对应卡片。
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Pagination, Popconfirm, Tooltip } from 'antd';
import { MapStatus } from '../utils/config';
import { AnchorLayerType, LightMapAssistType } from '@/utils/constant';
import { CLSTAG, EffectVehicleTypeText, WeekLimitText } from '@/utils/constant';
import { getAttributeIcon } from '@/utils/attributeIcon';
import { getAnchorLayerStyle } from '@/map-core';
import { clstagStr } from '../utils';
import {
  getNavigationEnumLabel,
  getNavigationDirectionLabels,
  type NavigationEnumOptions,
} from '../utils/navigation';
import { MAP_EDITOR_CONFIRM_POPUP_Z_INDEX } from '../utils/zIndex';
import { getEffectiveMapLabels } from '../utils/effectiveMap';

// 列表中重复使用的图标：提升到模块级避免每次渲染重新 require
const userIcon = require('@/assets/image/user_icon.png');
const vehicleIcon = require('@/assets/image/vehicle_icon.png');
const timeIcon = require('@/assets/image/time_icon.png');

interface OperateListProps {
  variableList: Array<any>;
  curSelected: any;
  pageData: { current: number; pageSize: number };
  changePageCb: (current: number, pageSize: number) => void;
  highlightMap: AnyFunc;
  editDynamicMap: AnyFunc;
  /** 路径规划模式：编辑入口置灰 */
  routePlanMode?: boolean;
  onDelete: (item: any) => void;
  updateMapStatus: (item: any) => void;
  navigationEnums: NavigationEnumOptions;
  getOverlayContainer: () => HTMLElement | null;
}

interface OperateListItemProps {
  item: any;
  isSelected: boolean;
  highlightMap: AnyFunc;
  editDynamicMap: AnyFunc;
  routePlanMode?: boolean;
  onDelete: (item: any) => void;
  updateMapStatus: (item: any) => void;
  navigationEnums: NavigationEnumOptions;
  getOverlayContainer: () => HTMLElement | null;
}

/** 格式化周期生效的展示文本 */
const formatEffectTime = (item: any) => {
  const weekText = item.weekLimit
    ? item.weekLimit.map((v: string) => WeekLimitText.get(v)).join('、')
    : '';
  const timeText = item.timeLimit
    ? item.weekLimit
      ? ',' + item.timeLimit
      : item.timeLimit
    : '';
  return `(${weekText}${timeText})`;
};

/** 单个操作记录卡片：独立 memo，避免整列表重渲染 */
const OperateListItem = React.memo(
  function OperateListItem({
    item,
    isSelected,
    highlightMap,
    editDynamicMap,
    routePlanMode,
    onDelete,
    updateMapStatus,
    navigationEnums,
    getOverlayContainer,
  }: OperateListItemProps) {
    const style = getAnchorLayerStyle(item?.type, item?.subType);

    /** 标签样式跨渲染稳定 */
    const badgeStyle = useMemo(
      () => ({
        background: style.listBadgeBackground,
        border: style.listBadgeBorder,
      }),
      [style]
    );

    /** Popconfirm/Tooltip 挂载到地图覆盖层容器，避免被地图区域裁剪 */
    const getLocalPopupContainer = useCallback(
      () => getOverlayContainer() ?? document.body,
      [getOverlayContainer]
    );

    /**
     * 点击卡片行。
     * 注意：Popconfirm 气泡通过 portal 挂载到 popup root，DOM 上不在卡片内，
     * 但 React 事件沿组件树冒泡，点气泡上的“确认/取消”也会触发本行的 onClick，
     * 需要按事件目标拦截，避免点气泡误选中卡片。
     */
    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest?.('.ant-popover')) return;
        highlightMap(isSelected ? null : item);
      },
      [highlightMap, isSelected, item]
    );

    /** 编辑 */
    const handleEdit = useCallback(
      (e: React.MouseEvent) => {
        if (isSelected) {
          e.stopPropagation();
          e.preventDefault();
        }
        editDynamicMap(item);
      },
      [editDynamicMap, isSelected, item]
    );

    /** 启用/停用 */
    const handleUpdateStatus = useCallback(() => {
      updateMapStatus(item);
    }, [updateMapStatus, item]);

    /** 删除 */
    const handleDelete = useCallback(() => {
      onDelete(item);
    }, [onDelete, item]);

    /** 阻止冒泡 */
    const stopPropagation = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
    }, []);

    return (
      <li
        className={`${isSelected ? 'selected' : 'unselected'} ${item.status}`}
        onClick={handleClick}
        clstag={clstagStr(CLSTAG.OperateCard)}
      >
        <div className={`record-content ${item.status}`}>
          <div className="remark-container">
            <div
              className={
                item.type === AnchorLayerType.ATTRIBUTE_ANNOTATION
                  ? 'attribute'
                  : 'others'
              }
              style={badgeStyle}
            >
              <div className="name">{style.listBadgeText}</div>
              {item.type === AnchorLayerType.ATTRIBUTE_ANNOTATION && (
                <div className="divider"></div>
              )}
              {item.type === AnchorLayerType.ATTRIBUTE_ANNOTATION &&
                item.subType && (
                <img src={getAttributeIcon(item.subType, 'list')} />
              )}
            </div>
            <Tooltip
              placement="top"
              title={item?.remark}
              getPopupContainer={getLocalPopupContainer}
            >
              <span className="remark">{item?.remark}</span>
            </Tooltip>
          </div>
          <div className="edit-wrapper">
            <Popconfirm
              placement="leftTop"
              title={`是否确认${
                item.status === MapStatus.RS_INUSE ? '停用' : '启用'
              }？该操作将影响车辆运行路线规划，请确定是否${
                item.status === MapStatus.RS_INUSE ? '停用' : '启用'
              }`}
              onConfirm={handleUpdateStatus}
              okText="确认"
              cancelText="取消"
              getPopupContainer={getLocalPopupContainer}
              overlayClassName="map-editor-confirm-popover"
              zIndex={MAP_EDITOR_CONFIRM_POPUP_Z_INDEX}
            >
              <span onClick={stopPropagation}>
                {item.status === MapStatus.RS_INUSE ? '停用' : '启用'}
              </span>
            </Popconfirm>
            {/* 路径规划模式下编辑入口禁用并提示；其他入口的兜底拦截在 useRightOperate guard */}
            {routePlanMode ? (
              <Tooltip
                placement="top"
                title="请先退出路径规划模式"
                getPopupContainer={getLocalPopupContainer}
              >
                <span className="edit-entry-disabled">编辑</span>
              </Tooltip>
            ) : (
              <span onClick={handleEdit}>编辑</span>
            )}

            <Popconfirm
              placement="leftTop"
              title="您确定是否删除该动态地图区域？"
              onConfirm={handleDelete}
              okText="确认"
              cancelText="取消"
              getPopupContainer={getLocalPopupContainer}
              overlayClassName="map-editor-confirm-popover"
              zIndex={MAP_EDITOR_CONFIRM_POPUP_Z_INDEX}
            >
              <span onClick={stopPropagation}>删除</span>
            </Popconfirm>
          </div>
        </div>

        <div className="operate-detail">
          <div className="line-one">
            <span className="operator-name">
              <img className="icon" src={userIcon}></img>
              {item?.operator}
            </span>

            <Tooltip
              placement="top"
              title={item?.vehicleLimit?.join(',')}
              getPopupContainer={getLocalPopupContainer}
            >
              <span className="effect-vehicle">
                <img className="icon" src={vehicleIcon}></img>
                {EffectVehicleTypeText.get(item.effectVehicleType)}
              </span>
            </Tooltip>
          </div>
          <div className="effect-time">
            <img className="icon" src={timeIcon}></img>
            <div>
              <span>{item?.effectDateTypeName}</span>
              {/* 周期生效 展示生效时间 */}
              {item?.effectDateType === 'CYCLICAL_TERM' && (
                <span className="time">{formatEffectTime(item)}</span>
              )}
            </div>
          </div>
          {item.type === AnchorLayerType.NAVIGATION && (
            <div className="navigation-detail">
              <span>
                拓扑：
                {getNavigationEnumLabel(
                  navigationEnums.topology,
                  item.topology
                )}
              </span>
              <span>
                方向：
                {getNavigationDirectionLabels(
                  navigationEnums.direction,
                  item.direction
                ) || '-'}
              </span>
            </div>
          )}
          {item.type === AnchorLayerType.ROAD_CLOSED &&
            item.effectiveMapList?.length > 0 && (
            <div className="effective-map-list">
              {getEffectiveMapLabels(item.effectiveMapList).map((label) => (
                <span className="effective-map-tag" key={label}>{label}</span>
              ))}
            </div>
          )}
          <div className={`status-container ${item.status}`}>
            <span>
              {item.status === MapStatus.RS_INUSE ? '启用中' : '停用中'}
            </span>
          </div>
        </div>
      </li>
    );
  }
);

const OperateList = ({
  variableList,
  curSelected,
  pageData,
  changePageCb,
  highlightMap,
  editDynamicMap,
  routePlanMode,
  onDelete,
  updateMapStatus,
  navigationEnums,
  getOverlayContainer,
}: OperateListProps) => {
  // 当前高亮的列表项，与地图选中态保持同步
  const [selectedItem, setSelectedItem] = useState<any>(null);

  /**
   * 当地图选中某个多边形时，自动翻页到该卡片所在页并高亮
   * 同时把 curSelected 同步到本地 selectedItem，保证双向同步
   */
  useEffect(() => {
    if (curSelected?.id && variableList?.length > 0) {
      const index = variableList.findIndex(
        (item: any) => item.id === curSelected.id
      );
      if (index >= 0) {
        changePageCb(
          Math.ceil((index + 1) / pageData.pageSize),
          pageData.pageSize
        );
      }
    }
    setSelectedItem(curSelected);
  }, [curSelected, variableList, pageData.pageSize]);

  // 根据当前页码切片得到本页展示数据
  const list = useMemo(() => {
    return variableList?.slice(
      pageData.pageSize * (pageData.current - 1),
      pageData.pageSize * pageData.current
    );
  }, [variableList, pageData.current, pageData.pageSize]);

  /** 分页变化回调 */
  const changePage = useCallback(
    (page: number, pageSize: number) => {
      changePageCb(page, pageSize);
    },
    [changePageCb]
  );

  /**
   * 点击卡片行：切换选中态并通知上层高亮地图对应多边形
   * 再次点击同一行则取消选中
   */
  const highlightRow = useCallback(
    (item: any) => {
      const s = selectedItem?.id === item?.id ? null : item;
      setSelectedItem(s);
      highlightMap && highlightMap(s);
    },
    [highlightMap, selectedItem]
  );

  /** Popconfirm/Tooltip 挂载到地图覆盖层容器 */
  const getLocalPopupContainer = useCallback(
    () => getOverlayContainer() ?? document.body,
    [getOverlayContainer]
  );

  return (
    <>
      <div className="operate-list-content">
        <ul className="map-list-content">
          {list?.map((item: any) => {
            const isSelected = selectedItem?.id === item?.id;
            return (
              <OperateListItem
                key={item.id}
                item={item}
                isSelected={isSelected}
                highlightMap={highlightRow}
                editDynamicMap={editDynamicMap}
                routePlanMode={routePlanMode}
                onDelete={onDelete}
                updateMapStatus={updateMapStatus}
                navigationEnums={navigationEnums}
                getOverlayContainer={getOverlayContainer}
              />
            );
          })}
        </ul>
      </div>

      <div className="page-content">
        {variableList?.length > 0 && (
          <Pagination
            defaultCurrent={1}
            current={pageData.current}
            pageSize={pageData.pageSize}
            pageSizeOptions={[10, 20, 50, 100]}
            total={variableList?.length ?? 0}
            onChange={changePage}
            simple
            showTotal={(total) => `共 ${total} 条`}
          />
        )}
      </div>
    </>
  );
};
export default React.memo(OperateList);
